import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { extractVariableIndices, MessageVariablesSection } from "./message-variables-section";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * SMS-specific fields. These are all `string` typed so they are compatible
 * with the parent TemplateFormData (whose `category` is a union `Category`
 * type).  The parent passes `handleChange` which accepts `Partial<TemplateFormData>`,
 * and since every field here is assignable to its TemplateFormData counterpart
 * there is no type conflict.
 */
export interface SmsFormData {
  templateName: string;
  category: string;
  language: string;
  messageBody: string;
  shortenUrl: boolean;
  extractedUrl: string;
  shortenedUrl: string;
  sampleValues: Record<string, string>;
}

export const defaultSmsFormData: SmsFormData = {
  templateName: "",
  category: "Marketing",
  language: "English (US)",
  messageBody: "",
  shortenUrl: false,
  extractedUrl: "",
  shortenedUrl: "",
  sampleValues: {},
};

/**
 * Use a generic onChange so the parent can pass its own wider handler
 * (e.g. `(updates: Partial<TemplateFormData>) => void`) without a type error.
 * The form only ever calls onChange with keys that exist on both types.
 */
interface SmsFormProps {
  formData: any;
  onChange: (updates: Record<string, any>) => void;
  authToken?: string;
  readOnly?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = ["Marketing", "Authentication", "Utility"];
const LANGUAGES = ["English (US)", "English (UK)", "Spanish", "French"];
const SMS_LIMIT = 160;
const MULTIPART_LIMIT = 153;

const URL_REGEX = /https?:\/\/[^\s]+/g;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractFirstUrl(text: string): string {
  const match = text.match(URL_REGEX);
  return match?.[0] ?? "";
}

function countSmsSegments(text: string) {
  const len = text.length;
  if (len === 0) return { chars: 0, segments: 1, remaining: SMS_LIMIT };
  if (len <= SMS_LIMIT) return { chars: len, segments: 1, remaining: SMS_LIMIT - len };
  const segments = Math.ceil(len / MULTIPART_LIMIT);
  const remaining = segments * MULTIPART_LIMIT - len;
  return { chars: len, segments, remaining };
}


// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">{label}</label>
);

const SelectInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: string[];
}> = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-transparent px-3 py-2 pr-8 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition-colors"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

// ─── Main SMS Form ────────────────────────────────────────────────────────────

export const SmsForm: React.FC<SmsFormProps> = ({ formData, onChange, authToken }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isShortening, setIsShortening] = useState(false);

  const { chars, segments } = countSmsSegments(formData.messageBody);

  // Auto-extract URL from body
  useEffect(() => {
    const url = extractFirstUrl(formData.messageBody);
    if (url !== formData.extractedUrl) {
      onChange({ extractedUrl: url, shortenedUrl: url ? formData.shortenedUrl : "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.messageBody]);

  // Same numbering as WhatsApp's MessageBodySection (message-section.tsx):
  // next index = highest {{n}} already in the body + 1. {{n}} placeholders
  // are never substituted away for SMS templates (reverted 2026-08-14 —
  // doing so broke the campaign composer's ability to detect a template's
  // variables at all once it had been saved with example values, since
  // there was nothing left in the text to scan for), so the body text alone
  // is always a reliable source for what's already used.
  const insertVariable = () => {
    const textarea = textareaRef.current;
    const indices = extractVariableIndices(formData.messageBody);
    const nextIndex = indices.length > 0 ? Math.max(...indices) + 1 : 1;
    const insertion = `{{${nextIndex}}}`;

    if (!textarea) {
      onChange({ messageBody: formData.messageBody + (formData.messageBody ? " " : "") + insertion });
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody =
      formData.messageBody.slice(0, start) + insertion + formData.messageBody.slice(end);
    onChange({ messageBody: newBody });
    setTimeout(() => {
      textarea.focus();
      const cursor = start + insertion.length;
      textarea.setSelectionRange(cursor, cursor);
    }, 0);
  };

  const handleShortenUrl = async () => {
    if (!formData.extractedUrl) return;
    setIsShortening(true);
    try {
      const token = authToken ?? localStorage.getItem("authToken");
      const encodedUrl = encodeURIComponent(formData.extractedUrl);
      const res = await fetch(`/campaign/track?url_link=${encodedUrl}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data?.status) {
        onChange({ shortenedUrl: data.shortened_url });
        toast.success(data.message || "Link shortened successfully!");
      } else {
        toast.error(data?.detail || "Failed to generate!");
      }
    } catch {
      toast.error("Failed to shorten URL");
    } finally {
      setIsShortening(false);
    }
  };

  const handleShortenToggle = (enabled: boolean) => {
    onChange({ shortenUrl: enabled });
    if (enabled && formData.extractedUrl && !formData.shortenedUrl) {
      handleShortenUrl();
    }
  };

  return (
    <div className="space-y-4">
      {/* Category + Language */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <SectionLabel label="Category" />
          <SelectInput value={formData.category} onChange={(v) => onChange({ category: v })} options={CATEGORIES} />
        </div>
        <div>
          <SectionLabel label="Language" />
          <SelectInput value={formData.language} onChange={(v) => onChange({ language: v })} options={LANGUAGES} />
        </div>
      </div>

      {/* Message Body — plain textarea, no highlight overlay (that overlay
          was the source of the "blurry" {{variable}} rendering: any tiny
          mismatch between its invisible text layer and the real textarea
          text below it shows up as ghosting. WhatsApp's own body editor
          (message-section.tsx) never used this trick either.) */}
      <div>
        <SectionLabel label="Message Body" />
        <div className="relative rounded-lg border border-gray-200 dark:border-gray-600 focus-within:ring-2 focus-within:ring-indigo-300 focus-within:border-indigo-400 transition-all bg-white dark:bg-transparent">
          <textarea
            ref={textareaRef}
            value={formData.messageBody}
            onChange={(e) => onChange({ messageBody: e.target.value })}
            placeholder="Type your SMS message here… Example: Hi {{1}}, your order {{2}} is ready!"
            rows={6}
            className="w-full px-3 pt-2.5 pb-10 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent resize-none focus:outline-none leading-relaxed"
          />

          {/* Bottom bar */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
            <span className={`text-xs ${chars > SMS_LIMIT ? "text-amber-500 dark:text-amber-400" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}`}>
              {chars} / {segments > 1 ? `${segments * MULTIPART_LIMIT} (${segments} SMS)` : `${SMS_LIMIT} Characters`}
            </span>
            <button
              type="button"
              onClick={insertVariable}
              className="text-xs font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1 hover:bg-white dark:hover:bg-gray-700 hover:shadow-sm transition-all"
            >
              Add variable
            </button>
          </div>
        </div>
      </div>

      {/* Name + example value per {{n}} — same shared component and
          experience as WhatsApp. {{n}} stays in the Message Body itself
          (never substituted); the example is resolved for display only in
          the Live Preview panel (create-template.tsx's getSmsPreviewProps). */}
      <MessageVariablesSection formData={formData} onChange={onChange} />

      {/* Shorten URL */}
      {formData.extractedUrl && (
        <div className="space-y-2">
          {/* Toggle row */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Shorten URL</span>
            <button
              type="button"
              role="switch"
              aria-checked={formData.shortenUrl}
              onClick={() => handleShortenToggle(!formData.shortenUrl)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-300 ${formData.shortenUrl ? "bg-indigo-500" : "bg-gray-200 dark:bg-gray-700"
                }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform ${formData.shortenUrl ? "translate-x-4.5" : "translate-x-0.5"
                  }`}
                style={{ transform: formData.shortenUrl ? "translateX(18px)" : "translateX(2px)" }}
              />
            </button>
          </div>

          {/* URL display */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5">
              <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 font-medium">Full:</span>
              <a
                href={formData.extractedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-700 dark:text-gray-300 font-medium truncate max-w-[180px] hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
              >
                {formData.extractedUrl}
              </a>
            </div>

            {formData.shortenUrl && (
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5">
                <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 font-medium">Short:</span>
                {isShortening ? (
                  <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 flex items-center gap-1">
                    <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    Shortening…
                  </span>
                ) : formData.shortenedUrl ? (
                  <a
                    href={formData.shortenedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                  >
                    {formData.shortenedUrl}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={handleShortenUrl}
                    className="text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                  >
                    Generate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Full modal wrapper (mirrors TemplateCreator structure) ───────────────────

interface SmsTemplateCreatorProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSaveDraft?: (data: SmsFormData) => void;
  onSendRequest?: (data: SmsFormData) => void;
  authToken?: string;
}

export const SmsTemplateCreator: React.FC<SmsTemplateCreatorProps> = ({
  isOpen = true,
  onClose,
  onSaveDraft,
  onSendRequest,
  authToken,
}) => {
  const [formData, setFormData] = useState<SmsFormData>(defaultSmsFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = useCallback(
    (updates: Partial<SmsFormData>) => setFormData((prev) => ({ ...prev, ...updates })),
    []
  );

  const validate = () => {
    if (!formData.templateName.trim()) { toast.error("Template name is required"); return false; }
    if (!formData.messageBody.trim()) { toast.error("Message body is required"); return false; }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!formData.templateName.trim()) { toast.error("Template name is required to save as draft"); return; }
    setIsSubmitting(true);
    setTimeout(() => {
      toast.success("Draft saved!");
      onSaveDraft?.(formData);
      setIsSubmitting(false);
    }, 600);
  };

  const handleSendRequest = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    setTimeout(() => {
      toast.success("Template submitted for review!");
      onSendRequest?.(formData);
      setIsSubmitting(false);
      onClose?.();
    }, 800);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full flex flex-col overflow-hidden"
        style={{ maxWidth: 996, maxHeight: "95vh" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">New template</h2>
            <p className="text-xs text-[#7B7B7B] font-semibold mt-0.5">Create a New Template</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#7B7B7B] font-semibold hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" width={18} height={18}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left: Form */}
          <div className="flex flex-col overflow-hidden flex-1">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Channel + Template name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <SectionLabel label="Channel" />
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 bg-white">
                    <div className="w-5 h-5 rounded bg-green-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-800 flex-1">SMS</span>
                    <svg className="w-4 h-4 text-[#7B7B7B] font-semibold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <div>
                  <SectionLabel label="Template Name" />
                  <input
                    type="text"
                    value={formData.templateName}
                    onChange={(e) => handleChange({ templateName: e.target.value })}
                    placeholder="Add a template name"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition-colors"
                  />
                </div>
              </div>

              <SmsForm formData={formData} onChange={handleChange} authToken={authToken} />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isSubmitting}
                className="text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-lg px-4 py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Saving…" : "Save as Draft"}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSendRequest}
                  disabled={isSubmitting}
                  className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-5 py-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending…" : "Save Template"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTemplateCreator;